import React, { KeyboardEvent, SyntheticEvent, useMemo, useState } from 'react';
import { useAppDispatch } from '../../../hooks';
import { resetPassword } from '../../../store/slices';
import { PrimaryButton, PrimaryInput } from '../../components/shared';
import { SvgSignal } from '../../../assets/svg/general';
import { useSearchParams } from 'react-router-dom';
import { ResetPasswordParams } from '../../../types';

const strengthChecks = [
  { label: '8 characters minimum', test: (p: string) => p.length >= 8 },
  { label: 'Lowercase', test: (p: string) => /[a-z]/.test(p) },
  { label: 'Uppercase', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'Number', test: (p: string) => /\d/.test(p) },
  { label: 'Symbol (e.g. @, #, $)', test: (p: string) => /[^A-Za-z0-9]/.test(p) },
];

const strengthLabels = ['Weak', 'Fair', 'Almost strong', 'Strong', 'Very strong'];
const strengthColors = ['#EF354C', '#F59E0B', '#F59E0B', '#5FC381', '#5FC381'];

const PasswordStrength = ({ password }: { password: string }) => {
  const passed = useMemo(
    () => strengthChecks.map((c) => c.test(password)),
    [password]
  );
  const score = passed.filter(Boolean).length;
  const barCount = 4;

  if (!password) return null;

  const activeColor = strengthColors[score - 1];

  return (
    <div className="mt-3 bg-slate-subtle rounded-xl p-2.5">
      <div className="flex items-center gap-1.5">
        <SvgSignal score={Math.ceil(score / 1.25)} activeColor={activeColor} />
        <span className="text-sm medium">{strengthLabels[score - 1] ?? ''}</span>
      </div>

      <div className="flex gap-1.5 mt-2">
        {Array.from({ length: barCount }).map((_, i) => (
          <div
            key={i}
            className="h-1.5 flex-1 rounded-full"
            style={{
              backgroundColor: i < score - 1 ? activeColor : '#E5E7EB',
            }}
          />
        ))}
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 mt-4">
        {strengthChecks.map((check, i) => (
          <div key={i} className="flex items-center gap-2">
            <div
              className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center ${
                passed[i] ? 'bg-[#5FC381]' : 'bg-neutral-300'
              }`}
            >
              <svg width={10} height={10} viewBox="0 0 10 10" fill="none">
                <path
                  d="M2 5L4 7L8 3"
                  stroke="white"
                  strokeWidth={1.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <span
              className={`text-xs ${
                passed[i] ? 'text-neutral-500 line-through' : 'text-neutral-500'
              }`}
            >
              {check.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const getCodeParam = () => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code') || '';
  return code.split('?')[0];
};

const ResetPassword = () => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const [required, setRequired] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [payload, setPayload] = useState<ResetPasswordParams>({
    code: getCodeParam() || searchParams.get('code') || '',
    password: '',
    confirmPassword: '',
  });

  const _handleReset = (e?: SyntheticEvent<HTMLFormElement>) => {
    e?.preventDefault();
    if (!required) return;

    setLoading(true);
    dispatch(resetPassword({ payload })).finally(() => setLoading(false));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      _handleReset();
    }
  };

  const onGetInputValue = (event: SyntheticEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    const name = event.currentTarget.name;

    setPayload({ ...payload, [name]: value });
  };

  React.useEffect(() => {
    const { code, password, confirmPassword } = payload;
    const passedCount = strengthChecks.filter((c) => c.test(password)).length;
    setRequired(
      code.length > 0 &&
        password.length > 0 &&
        confirmPassword.length > 0 &&
        confirmPassword === password &&
        passedCount >= 4
    );
  }, [payload]);

  React.useEffect(() => {
    const code = searchParams.get('code');
    if (code) {
      setPayload((prev) => ({ ...prev, code }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <div className="h-screen bg-neutral-200 flex items-center justify-center">
      <form
        className="flex flex-col items-stretch bg-white rounded-2xl w-full max-w-md p-10 shadow-lg"
        onSubmit={_handleReset}
      >
        <h1 className="text-neutral-800 bold text-center text-2xl mt-4">
          Reset password
        </h1>
        <p className="text-neutral-500 text-center text-sm mt-2">
          Setup new password to secure your account and log in
        </p>

        <PrimaryInput
          password
          onChange={onGetInputValue}
          onKeyDown={handleKeyDown}
          placeholder="Enter new password"
          name="password"
          label="Create new password"
          className="w-full"
          type="password"
        />

        <PasswordStrength password={payload.password} />

        <PrimaryInput
          password
          onChange={onGetInputValue}
          onKeyDown={handleKeyDown}
          placeholder="Re-enter password"
          name="confirmPassword"
          label="Confirm password"
          className="w-full"
          type="password"
        />

        <PrimaryButton
          disabled={!required || loading}
          loading={loading}
          text="Create password"
          type="submit"
          classNames="mt-8 medium"
        />
      </form>
    </div>
  );
};

export default ResetPassword;
